"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function ServiceTermsEditorial() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = ["Definitions", "Services Scope", "Pricing", "Payment Terms", "Installation Terms", "Maintenance Terms", "Warranty", "Liability Limitations", "Intellectual Property", "Confidentiality", "Termination", "Dispute Resolution", "Changes to Terms", "Governing Law"];
  const termsSections = [{
    title: "Definitions",
    content: "Coastline Air Conditioning Services Ltd (the 'Company') provides professional climate control solutions. These terms govern the relationship between the Company and the Client for all services rendered at Merrydown Kingston Lane East Preston BN16 1RP or client sites."
  }, {
    title: "Services Scope",
    content: "Our services include installation, maintenance, and repair of air conditioning systems. All work is performed to industry standards by qualified technicians."
  }, {
    title: "Pricing",
    content: "Quotes are valid for 30 days. Prices are subject to change based on site conditions or equipment availability, with prior notification to the client."
  }, {
    title: "Payment Terms",
    content: "Invoices are payable within 14 days of issue. Late payments may incur interest charges as per statutory regulations."
  }, {
    title: "Installation Terms",
    content: "Clients must ensure clear access to the site. Coastline Air Conditioning Services Ltd is not responsible for pre-existing structural issues discovered during installation."
  }, {
    title: "Maintenance Terms",
    content: "Scheduled maintenance plans are essential for warranty validity. We provide comprehensive checks to ensure optimal energy efficiency."
  }, {
    title: "Warranty",
    content: "New installations carry a manufacturer's warranty plus our standard workmanship guarantee. Contact info@coastline-services.co.uk for specific claim details."
  }, {
    title: "Liability Limitations",
    content: "Liability is limited to the total value of the contract. We are not liable for indirect or consequential losses resulting from equipment downtime."
  }, {
    title: "Intellectual Property",
    content: "All technical drawings, designs, and documentation provided by the Company remain our intellectual property."
  }, {
    title: "Confidentiality",
    content: "Both parties agree to keep sensitive commercial information confidential throughout the duration of the agreement and thereafter."
  }, {
    title: "Termination",
    content: "Contracts may be terminated with 30 days' written notice. Outstanding balances become due immediately upon termination."
  }, {
    title: "Dispute Resolution",
    content: "Any disputes shall first be addressed through formal mediation before proceeding to legal action."
  }, {
    title: "Changes to Terms",
    content: "Coastline Air Conditioning Services Ltd reserves the right to update these terms. The effective date of the current version is October 2023."
  }, {
    title: "Governing Law",
    content: "These terms are governed by the laws of England and Wales. Any legal proceedings shall be held in the appropriate local jurisdiction."
  }];
  return <section data-section-id="5147" id="servicetermseditorial" className="bg-background text-foreground relative w-full min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen relative">
        <div aria-hidden="true" className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-border z-10" />
        <div aria-hidden="true" className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
          <div className="size-3 bg-primary" />
        </div>

        <div ref={leftRef} className={`relative bg-background px-6 py-16 sm:px-12 sm:py-20 lg:px-24 lg:py-24 flex flex-col justify-center transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-12 lg:mb-16">
            <span className="font-default text-[11px] tracking-[0.2em] uppercase text-primary">
              — TERMS OF SERVICE
            </span>
          </div>

          <div className="mb-10">
            <h1 className="font-default text-4xl font-bold mb-6">Service Terms & Conditions</h1>
            
            <div className="space-y-12">
              {termsSections.map((section, i) => <div key={i} data-index={i} className="group">
                  <h2 className="font-default text-xl font-semibold mb-4 text-primary uppercase tracking-wider">
                    {i + 1}. {section.title}
                  </h2>
                  <div className="font-default text-[16px] leading-[1.7] text-foreground/90">
                    {i === 0 && <span className="float-left font-default text-primary text-[6rem] leading-[0.85] pr-4 pt-2 -ml-1">
                        C
                      </span>}
                    {section.content}
                  </div>
                </div>)}
            </div>
          </div>

          <div className="w-full h-px bg-border mb-8" />

          <div className="mb-12">
            <h3 className="font-default text-[12px] tracking-[0.2em] uppercase text-foreground/60 mb-6">Quick Navigation</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, i) => <li data-index={i} key={i} className="flex items-start gap-3 font-default text-[11px] tracking-[0.15em] uppercase text-foreground/80">
                  <span className="text-primary select-none">—</span>
                  <span>{feature}</span>
                </li>)}
            </ul>
          </div>

          <div>
            <Button asChild variant="outline" size="lg" className="bg-transparent border border-foreground text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary font-default text-[12px] tracking-[0.2em] uppercase px-8 h-12 transition-colors duration-300 rounded-lg">
              
            </Button>
          </div>
        </div>

        <div ref={rightRef} className={`relative min-h-[60vh] lg:min-h-screen overflow-hidden transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/mechcool_climate_control_service.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/mechcool_climate_control_service.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/mechcool_climate_control_service.png-wvc-sizes" alt="Professional air conditioning maintenance documentation and service terms" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full max-w-full object-cover rounded-lg shadow-lg" />
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.15] sm:opacity-[0.08] mix-blend-overlay pointer-events-none" style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")"
        }} />

          <div className="absolute bottom-6 right-6 sm:bottom-10 sm:right-10 border border-foreground/90 bg-background/40 backdrop-blur-sm px-4 py-3 shadow-lg rounded-lg">
            <span className="font-default text-[10px] tracking-[0.2em] uppercase text-foreground">
              Service / Climate / Coastline Air Conditioning Services Ltd
            </span>
          </div>
        </div>
      </div>
    </section>;
}